import type { ComponentPropsWithRef } from 'react'

import { cn } from '@/shared/lib'

/**
 * State is never communicated by colour or an icon alone — it is a dot plus a
 * label. The plate follows the fixed recipe: background at 14% of the hue,
 * border at 28%, text 20% lighter than the base, dot at full strength.
 */
const TONES = {
  success: 'border-status-success-bd bg-status-success-bg text-success-text',
  warning: 'border-status-warning-bd bg-status-warning-bg text-warning-text',
  danger: 'border-status-danger-bd bg-status-danger-bg text-destructive-text',
  info: 'border-status-info-bd bg-status-info-bg text-info-text',
  neutral: 'border-status-neutral-bd bg-status-neutral-bg text-fg-secondary',
} as const

const DOTS = {
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-destructive',
  info: 'bg-info',
  neutral: 'bg-muted-foreground',
} as const

type StatusBadgeProps = ComponentPropsWithRef<'span'> & {
  tone?: keyof typeof TONES
  size?: 'sm' | 'md'
  /** Pulses the dot — for a state that is still moving, not a decoration. */
  isLive?: boolean
}

export function StatusBadge({
  className,
  tone = 'neutral',
  size = 'md',
  isLive = false,
  children,
  ...props
}: StatusBadgeProps) {
  return (
    <span
      data-slot="status-badge"
      className={cn(
        'inline-flex items-center whitespace-nowrap border font-medium',
        size === 'sm'
          ? 'h-5.5 gap-1.5 rounded-full px-2.5 text-label'
          : 'h-6.5 gap-[7px] rounded-full px-3 text-caption',
        TONES[tone],
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          'shrink-0 rounded-full',
          size === 'sm' ? 'size-1.25' : 'size-1.5',
          DOTS[tone],
          isLive && 'animate-live',
        )}
      />
      {children}
    </span>
  )
}
